import { Box, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  getKibanaAnalyticsSetting,
  getKibanaWorkspaceAnalytics,
  IKibanaAnalyticsSettings,
  IKibanaAnalyticsSettingsPayload,
  IKibanaWorkspaceAnalyticsSettings,
  saveKibanaAnalyticsSettings,
} from 'wisestep-api';
import { ChipsType, Label, WsButton, WsTagsList } from 'wisestep-base';

import { SettingsPageLayout } from '../../../components/SettingsPageLayout';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../../features/teams/helper/defaultValues';
import { warning, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { HeaderBox } from '../components/HeaderBox';

import ModifySettingsDialog from './ModifySettingsDialog';
const BoldLabel = styled(Label)(() => ({
  fontWeight: 'bold',
}));
const AnalyticsSettingsPage = () => {
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [edit, setEdit] = useState({});
  const [showDialog, setShowDialog] = useState(false);
  const [kibanaAnalyticsSettingsData, setKibanaAnalyticsSettingsData] =
    useState<IKibanaAnalyticsSettings[]>();
  const [kibanaWorkspaceAnalyticsData, setKibanaWorkspaceAnalyticsData] =
    useState<IKibanaWorkspaceAnalyticsSettings[]>();
  const methods = useForm();
  useEffect(() => {
    (async function () {
      const getKibanaAnalyticsSettingData = await getKibanaAnalyticsSetting();
      const getKibanaWorkspaceAnalyticsData = await getKibanaWorkspaceAnalytics();
      if (getKibanaAnalyticsSettingData.data && getKibanaWorkspaceAnalyticsData.data) {
        setKibanaAnalyticsSettingsData(getKibanaAnalyticsSettingData.data);
        setKibanaWorkspaceAnalyticsData(getKibanaWorkspaceAnalyticsData.data);
      }
    })();
  }, []);

  const onEdit = (data) => {
    setEdit(data);
    setShowDialog(true);
  };

  const saveToAPI = async (data) => {
    const payload: IKibanaAnalyticsSettingsPayload = {
      dashboard: data.dashboard ? data.dashboard : false,
      name: data.name,
      workspaceLevelName: data.workspaceLevelName,
      roles: data.roles,
    };
    try {
      await saveKibanaAnalyticsSettings(payload);
      setNotification(success('Analytics Settings updated successfully'));
    } catch (error) {
      setNotification(warning('Something Went Wrong , Please try again'));
    }
  };

  return (
    <SettingsPageLayout
      title={'Analytics Settings'}
      indexOptions={[{ label: 'Analytics Settings', url: '/analytics-settings' }]}
      breadCrumnbsList={getSettingBreadCrumbList(
        ISettingBreadCrumbTypes.ANALYTICS,
        Pages['ANALYTICS_SETTINGS']
      )}
    >
      <Box px={8} pt={2}>
        <HeaderBox title={'Your Analytics Settings'} includeBtn={false} />
        <FormProvider {...methods}>
          <Grid container direction="column" spacing={2}>
            <Grid item xs={12} container direction="row" mb={1}>
              <Grid item xs={4}>
                <BoldLabel>Name</BoldLabel>
              </Grid>
              <Grid item xs={6}>
                <BoldLabel>Roles</BoldLabel>
              </Grid>
              <Grid item xs={2} display="flex" justifyContent="center">
                <BoldLabel>Customization</BoldLabel>
              </Grid>
            </Grid>

            {kibanaAnalyticsSettingsData?.map((single) => (
              <Grid item xs={12} container direction="row" key={single.id}>
                <Grid item xs={4}>
                  <Label sx={{ wordWrap: 'break-word' }}>{single.name}</Label>
                </Grid>
                <Grid item xs={6}>
                  <WsTagsList chipArray={single.roles} count={5} type={ChipsType.PRIMARY} />
                </Grid>
                <Grid item xs={2} display="flex" justifyContent="center">
                  <WsButton onClick={() => onEdit(single)}>Edit</WsButton>
                </Grid>
              </Grid>
            ))}
          </Grid>

          <Grid marginTop={2} container xs={12} direction="column" spacing={2}>
            <HeaderBox title={'Default Analytics Settings'} includeBtn={false} />
            <Grid item xs={12} container direction="row" mb={1}>
              <Grid item xs={4}>
                <BoldLabel>Name</BoldLabel>
              </Grid>
              <Grid item xs={6}>
                <BoldLabel>Roles</BoldLabel>
              </Grid>
              <Grid item xs={2} display="flex" justifyContent="center">
                <BoldLabel>Customization</BoldLabel>
              </Grid>
            </Grid>

            {kibanaWorkspaceAnalyticsData?.map((option, index) => (
              <Grid item xs={12} container direction="row" key={index}>
                <Grid item xs={4}>
                  <Label>{option.name}</Label>
                </Grid>
                <Grid item xs={6}>
                  <WsTagsList chipArray={option.roles} count={5} type={ChipsType.PRIMARY} />
                </Grid>
                <Grid item xs={2} display="flex" justifyContent="center">
                  <WsButton onClick={() => onEdit(option)}>Edit</WsButton>
                </Grid>
              </Grid>
            ))}
          </Grid>
          <ModifySettingsDialog
            openDialog={showDialog}
            closeDialog={() => setShowDialog(false)}
            handler={(payload) => saveToAPI(payload)}
            data={edit}
          />
        </FormProvider>
      </Box>
    </SettingsPageLayout>
  );
};

export default AnalyticsSettingsPage;
